import React from "react";
import { cn } from "@/lib/format";

// ---------- generic table (desktop) + stacked cards (mobile) ----------
export const Table = ({ columns, rows, empty = "No data.", loading = false, skeletonRows = 5 }) => (
  <div className="border border-border/60 bg-card/40">
    {/* Desktop / tablet: real table with contained horizontal scroll */}
    <div className="hidden md:block overflow-x-auto">
      <table className="w-full min-w-[560px] text-[13px] mono tabnum">
        <thead>
          <tr className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
            {columns.map((c) => (
              <th
                key={c.key}
                className={cn(
                  "text-left font-sans font-medium px-3 py-2 border-b border-border/60 sticky top-0 bg-card/80 backdrop-blur",
                  c.align === "right" && "text-right",
                )}
              >
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {loading && rows.length === 0 ? (
            Array.from({ length: skeletonRows }).map((_, i) => (
              <tr
                key={`skeleton-${i}`}
                data-testid="row-skeleton"
                className="border-b border-border/30 last:border-b-0"
              >
                {columns.map((c) => (
                  <td
                    key={c.key}
                    className={cn("px-3 py-2.5 align-top", c.align === "right" && "text-right")}
                  >
                    <span
                      className={cn(
                        "inline-block h-3 bg-muted/30 animate-pulse rounded-sm",
                        c.align === "right" ? "w-16" : "w-32",
                      )}
                    />
                  </td>
                ))}
              </tr>
            ))
          ) : rows.length === 0 ? (
            <tr>
              <td colSpan={columns.length} className="px-3 py-8 text-center text-muted-foreground font-sans">
                {empty}
              </td>
            </tr>
          ) : (
            rows.map((r, i) => (
              <tr
                key={i}
                className="hover:bg-muted/20 transition-colors border-b border-border/30 last:border-b-0"
              >
                {columns.map((c) => (
                  <td
                    key={c.key}
                    className={cn("px-3 py-2 align-top", c.align === "right" && "text-right")}
                  >
                    {c.render ? c.render(r) : r[c.key]}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>

    {/* Mobile: stacked label / value cards */}
    <div className="md:hidden divide-y divide-border/40">
      {loading && rows.length === 0 ? (
        Array.from({ length: skeletonRows }).map((_, i) => (
          <div key={`mskel-${i}`} data-testid="row-skeleton" className="px-4 py-3 space-y-2">
            <div className="h-3 w-1/2 bg-muted/30 animate-pulse rounded-sm" />
            <div className="h-3 w-2/3 bg-muted/30 animate-pulse rounded-sm" />
          </div>
        ))
      ) : rows.length === 0 ? (
        <div className="px-4 py-8 text-center text-muted-foreground font-sans text-[13px]">{empty}</div>
      ) : (
        rows.map((r, i) => (
          <div key={i} data-testid="table-card" className="px-4 py-3 space-y-1.5">
            {columns.map((c) => (
              <div key={c.key} className="flex items-start justify-between gap-3">
                <span className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-sans pt-0.5 shrink-0">
                  {c.label}
                </span>
                <span className="text-[13px] mono tabnum text-right min-w-0 break-all">
                  {c.render ? c.render(r) : r[c.key]}
                </span>
              </div>
            ))}
          </div>
        ))
      )}
    </div>
  </div>
);
